import { NgModule, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { HttpModule } from '@angular/http';
import { AppComponent } from './app.component';
import { RouterModule } from '@angular/router';
import { appRoutes } from './app.routes';
import { appEffects } from './app.effects';
import { Angular2TokenService } from 'angular2-token';
import { DateValueAccessorModule } from 'angular-date-value-accessor';
import { Ng2CompleterModule } from 'ng2-completer';
import { OrderModule } from 'ngx-order-pipe';
import { CalendarModule } from 'angular-calendar';
import { ClickOutsideModule } from 'ng-click-outside';

import {
  SignInIffNotAuthorisedGuard,
  RedirectToMainIfSignedIn,
} from './services/router-guard';
import { ConstantsHelpers } from './constants/helpers';
import { MainComponent } from '@components/main';
import { MainNavigationComponent } from '@components/main-navigation';
import { ContactListComponent } from '@components/contact-list';
import { ContactSearchParamsComponent } from '@components/contact-search-params';
import { ContactFormComponent } from '@components/contact-form';
import { ContactViewComponent } from '@components/contact-view';
import { EnquiriesListComponent } from '@components/enquiries-list';
import { EnquiryViewComponent } from '@components/enquiry-view';
import { EnquiryFormComponent } from '@components/enquiry-form';
import { ClassManagerViewComponent } from '@components/class-manager-view';
import { ClassesTimetableViewComponent } from '@components/classes-timetable-view';
import { ClassesListViewComponent } from '@components/classes-list-view';
import { SetClassFormComponent } from '@components/set-class-form';
import { ClassViewComponent } from '@components/class-view';
import { EnrolmentTimetableViewComponent } from '@components/enrolment-timetable-view';
import { EnrolmentFormComponent } from '@components/enrolment-form';
import { CalendarViewComponent } from '@components/calendar-view';
import { CalendarDayFormComponent } from '@components/calendar-day-form';
import { AbsenceFormComponent } from '@components/absence-form';
import { WorkshopsViewComponent } from '@components/workshops-view';
import { WorkshopsTimetableViewComponent } from '@components/workshops-timetable-view';
import { WorkshopCampusFormComponent } from '@components/workshop-campus-form';
import { WindowFormComponent } from '@components/window-form';
import { TutorAllocationsComponent } from '@components/tutor-allocations';
import { TutorAllocationFormComponent } from '@components/tutor-allocation-form';
import { WorkshopBookingsComponent } from '@components/workshop-bookings';
import { TimeslotsTableComponent } from '@components/timeslots-table';
import { BookingFormComponent } from '@components/booking-form';
import { WorkshopAbsenceFormComponent } from '@components/workshop-absence-form';
import { BookingRemovalFormComponent } from '@components/booking-removal-form';
import { EventsListComponent } from '@components/events-list';
import { EventFormComponent } from '@components/event-form';
import { EventViewComponent } from '@components/event-view';
import { PurchaseTicketComponent } from '@components/public/purchase-ticket';
import { PurchaseTicketFormComponent } from '@components/public/purchase-ticket-form';
import { PurchaseTicketGuestFormComponent } from '@components/public/purchase-ticket-guest-form';
import { PurchaseTicketExistingFormComponent } from '@components/public/purchase-ticket-existing-form';
import { CreditCardFormComponent } from '@components/public/credit-card-form';
import { RolesViewComponent } from '@components/roles-view';
import { ContactDocumentsManagerComponent } from '@components/contact-documents-manager';
import { ContactNotesManagerComponent } from '@components/contact-notes-manager';
import { WebcamComponent } from '@components/webcam';
import { OnlineEnrolmentViewComponent } from '@components/online-enrolment-view';
import { OnlineEnrolmentQuickDetailsComponent } from '@components/online-enrolment-quick-details';
import { OnlineEnrolmentTimelineComponent } from '@components/online-enrolment-timeline';
import { OnlineEnrolmentStepOneComponent } from '@components/online-enrolment-step-one';
import { OnlineEnrolmentStepTwoComponent } from '@components/online-enrolment-step-two';
import { OnlineEnrolmentStepThreeComponent } from '@components/online-enrolment-step-three';
import { OnlineEnrolmentStepFourComponent } from '@components/online-enrolment-step-four';
import { AdminAreaViewComponent } from '@components/admin-area-view';
import { ClassRolloverViewComponent } from '@components/class-rollover-view';
import { WorkshopsRolloverViewComponent } from '@components/workshops-rollover-view';
import { EnrolmentRolloverViewComponent } from '@components/enrolment-rollover-view';
import { SetRolloverFormComponent } from '@components/set-rollover-form';
import { AcademicRolloverViewComponent } from '@components/academic-rollover-view';
import { ChangeClassFormComponent } from '@components/change-class-form';
import { DisenrolmentFormComponent } from '@components/disenrolment-form';
import { DebtorTransactionsListViewComponent } from '@components/debtor-transactions-list-view';
import { CreateTransactionViewComponent } from '@components/create-transaction-view';
import { CreateTransactionFormComponent } from '@components/create-transaction-form';
import { AddDiscountFormComponent } from '@components/add-discount-form';
import { TransactionsViewComponent } from '@components/transactions-view';
import { TransactionLineItemComponent } from '@components/transaction-line-item';
import { DirectDebitSummaryViewComponent } from '@components/direct-debit-summary-view';
import { TransactionsByDebtorViewComponent } from '@components/transactions-by-debtor-view';
import { CommsViewComponent } from '@components/comms-view';
import { CommsSendsViewComponent } from '@components/comms-sends-view';
import { CommsSmsViewComponent } from '@components/comms-sms-view';
import { CommsFormComponent } from '@components/comms-form';
import { TicketFormComponent } from '@components/ticket-form';
import { ModalComponent } from '@components/modal';
import { InitialResolver } from './resolvers/initial';
import { ContactViewResolver } from './resolvers/contact-view';
import { EnquiryViewResolver } from './resolvers/enquiry-view';
import { ClassManagerViewResolver } from './resolvers/class-manager-view';
import { ClassViewResolver } from './resolvers/class-view';
import { EnrolmentTimetableViewResolver } from './resolvers/enrolment-timetable-view';
import { WorkshopsViewResolver } from './resolvers/workshops-view';
import { InternalEventViewResolver } from './resolvers/internal-event-view';
import { EventsViewResolver } from './resolvers/events-view';
import { EventViewResolver } from './resolvers/event-view';
import { RolesViewResolver } from './resolvers/roles-view';
import { CalendarViewResolver } from './resolvers/calendar-view';
import { OnlineEnrolmentViewResolver } from './resolvers/online-enrolment-view';
import { DirectDebitSummaryViewResolver } from './resolvers/direct-debit-summary-view';
import { TransactionsByDebtorViewResolver } from './resolvers/transactions-by-debtor-view';
import { CommsViewResolver } from './resolvers/comms-view';
import { ConstantService } from './services/constant';
import { ModalService } from './services/modal';
import { ContactService } from './services/contact';
import { EnquiryService } from './services/enquiry';
import { MatrixClassService } from './services/matrix-class';
import { EnrolmentService } from './services/enrolment';
import { TermService } from './services/term';
import { PublicHolidayService } from './services/public-holiday';
import { WorkshopService } from './services/workshop';
import { TutorAllocationService } from './services/tutor-allocation';
import { BookingService } from './services/booking';
import { EventService } from './services/event';
import { TicketService } from './services/ticket';
import { RoleService } from './services/role';
import { TransactionService } from './services/transaction';
import { CommService } from './services/comm';
import { ActionCableService } from './services/action-cable';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { rootReducer } from '@reducers/combined-reducer';
import { UniqueEmailAsyncValidation } from '@components/validators/unique-email-async';
import { PolicyCheckDirective } from './policy-check.directive';

@NgModule({
  imports: [
    BrowserModule,
    StoreModule.provideStore(rootReducer),
    StoreDevtoolsModule.instrumentOnlyWithExtension({
      maxAge: 30,
    }),
    RouterModule.forRoot(appRoutes),
    ...appEffects,
    FormsModule,
    ReactiveFormsModule,
    HttpModule,
    BrowserAnimationsModule,
    CalendarModule.forRoot(),
    Ng2CompleterModule,
    DateValueAccessorModule,
    OrderModule,
    ClickOutsideModule,
  ],
  declarations: [
    AppComponent,
    ModalComponent,
    MainComponent,
    MainNavigationComponent,
    ContactListComponent,
    ContactSearchParamsComponent,
    ContactFormComponent,
    ContactViewComponent,
    EnquiriesListComponent,
    EnquiryFormComponent,
    EnquiryViewComponent,
    ClassManagerViewComponent,
    ClassesTimetableViewComponent,
    ClassesListViewComponent,
    SetClassFormComponent,
    ClassViewComponent,
    EnrolmentTimetableViewComponent,
    EnrolmentFormComponent,
    CalendarViewComponent,
    CalendarDayFormComponent,
    AbsenceFormComponent,
    WorkshopsViewComponent,
    WorkshopsTimetableViewComponent,
    WorkshopCampusFormComponent,
    WindowFormComponent,
    TutorAllocationsComponent,
    TutorAllocationFormComponent,
    WorkshopBookingsComponent,
    TimeslotsTableComponent,
    BookingFormComponent,
    WorkshopAbsenceFormComponent,
    BookingRemovalFormComponent,
    EventsListComponent,
    EventFormComponent,
    EventViewComponent,
    PurchaseTicketComponent,
    PurchaseTicketFormComponent,
    PurchaseTicketGuestFormComponent,
    PurchaseTicketExistingFormComponent,
    CreditCardFormComponent,
    RolesViewComponent,
    WebcamComponent,
    ContactDocumentsManagerComponent,
    ContactNotesManagerComponent,
    OnlineEnrolmentViewComponent,
    OnlineEnrolmentQuickDetailsComponent,
    OnlineEnrolmentTimelineComponent,
    OnlineEnrolmentStepOneComponent,
    OnlineEnrolmentStepTwoComponent,
    OnlineEnrolmentStepThreeComponent,
    OnlineEnrolmentStepFourComponent,
    AdminAreaViewComponent,
    ClassRolloverViewComponent,
    WorkshopsRolloverViewComponent,
    EnrolmentRolloverViewComponent,
    SetRolloverFormComponent,
    ChangeClassFormComponent,
    DisenrolmentFormComponent,
    AcademicRolloverViewComponent,
    DebtorTransactionsListViewComponent,
    CreateTransactionViewComponent,
    CreateTransactionFormComponent,
    AddDiscountFormComponent,
    TicketFormComponent,
    TransactionsViewComponent,
    TransactionLineItemComponent,
    DirectDebitSummaryViewComponent,
    TransactionsByDebtorViewComponent,
    CommsViewComponent,
    CommsSendsViewComponent,
    CommsSmsViewComponent,
    CommsFormComponent,
    UniqueEmailAsyncValidation,
    PolicyCheckDirective,
  ],
  entryComponents: [
    ContactFormComponent,
    EnquiryFormComponent,
    SetClassFormComponent,
    EnrolmentFormComponent,
    CalendarDayFormComponent,
    AbsenceFormComponent,
    WorkshopCampusFormComponent,
    WindowFormComponent,
    TutorAllocationFormComponent,
    BookingFormComponent,
    WorkshopAbsenceFormComponent,
    BookingRemovalFormComponent,
    SetRolloverFormComponent,
    ChangeClassFormComponent,
    DisenrolmentFormComponent,
    CreateTransactionViewComponent,
    CreateTransactionFormComponent,
    AddDiscountFormComponent,
    TicketFormComponent,
  ],
  providers: [
    ActionCableService,
    Angular2TokenService,
    SignInIffNotAuthorisedGuard,
    RedirectToMainIfSignedIn,
    ConstantsHelpers,
    InitialResolver,
    ContactViewResolver,
    EnquiryViewResolver,
    ClassManagerViewResolver,
    ClassViewResolver,
    EnrolmentTimetableViewResolver,
    WorkshopsViewResolver,
    InternalEventViewResolver,
    EventsViewResolver,
    EventViewResolver,
    RolesViewResolver,
    CalendarViewResolver,
    OnlineEnrolmentViewResolver,
    DirectDebitSummaryViewResolver,
    TransactionsByDebtorViewResolver,
    CommsViewResolver,
    ConstantService,
    ModalService,
    ContactService,
    EnquiryService,
    MatrixClassService,
    EnrolmentService,
    TermService,
    PublicHolidayService,
    WorkshopService,
    TutorAllocationService,
    BookingService,
    EventService,
    TicketService,
    RoleService,
    TransactionService,
    CommService,
    UniqueEmailAsyncValidation,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}



// WEBPACK FOOTER //
// /Users/jason/src/matrix/client/src/app/app.module.ts